import { useEffect, useRef, useState } from 'react';
import { readOutline, writeOutline } from './outlineCache';

const READ_DEBOUNCE_MS = 250;
/** A heading is "current" once its top passes this far below the viewport's top. */
const ACTIVE_OFFSET_PX = 96;

/** The element that scrolls the document (OverlayScrollbars' viewport, else the nearest scroller). */
export function scrollerOf(el) {
  const viewport = el?.closest?.('[data-overlayscrollbars-viewport]');
  if (viewport) return viewport;
  let cur = el?.parentElement;
  while (cur) {
    const { overflowY } = window.getComputedStyle(cur);
    if (overflowY === 'auto' || overflowY === 'scroll') return cur;
    cur = cur.parentElement;
  }
  return null;
}

/**
 * The open document's h2/h3 outline and the section being read (card 2.4).
 *
 * Headings are read from the editor's DOM (their ids come from
 * HeadingIdsPlugin) whenever the body changes — `subscribeBody`, debounced.
 * The current section follows scrolling: on every scroll of the document's
 * scroller (one pass per frame), the current heading is the last one whose
 * top is above `ACTIVE_OFFSET_PX`.
 *
 * State survives the rail's unmount (owner, 14.10.2026): it starts from what
 * the rail last showed for this document (outlineCache) and refreshes
 * quietly, so reopening a tab or toggling the rail does not blink. `items`
 * is `null` until the document has been read once — "unknown", not "no
 * headings" — so the empty hint never flashes before the real list.
 *
 * @returns {{ items: Array<{id, text, level}>|null, activeId: string|null }}
 */
export function useOutline({ documentId, bodyRef, subscribeBody, enabled }) {
  const [items, setItems] = useState(() => readOutline(documentId)?.items ?? null);
  const [activeId, setActiveId] = useState(() => readOutline(documentId)?.activeId ?? null);
  const headingsRef = useRef([]);
  // Bumped on every read of the body: the cached `items` keep their identity
  // across a remount, so the observer below re-attaches on this instead (the
  // heading elements are new even when their text is not).
  const [readTick, setReadTick] = useState(0);

  useEffect(() => {
    writeOutline(documentId, { items, activeId });
  }, [documentId, items, activeId]);

  useEffect(() => {
    if (!enabled || !subscribeBody) return undefined;
    let timer = null;
    const read = (initial = false) => {
      const root = bodyRef.current?.querySelector('.lexical-content');
      // The first read may land before a reopened document has rendered its
      // body: an editor with no text yet says nothing — keep what the rail
      // already shows. Reads after a body change are always authoritative.
      if (initial && !root?.textContent.trim() && readOutline(documentId)?.items?.length) return;
      const nodes = root ? Array.from(root.querySelectorAll('h2, h3')) : [];
      headingsRef.current = nodes;
      setReadTick((n) => n + 1);
      setItems((prev) => {
        const next = nodes
          .filter((el) => el.id && el.textContent.trim())
          .map((el) => ({ id: el.id, text: el.textContent.trim(), level: el.tagName === 'H3' ? 3 : 2 }));
        return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
      });
    };
    const schedule = () => {
      clearTimeout(timer);
      timer = setTimeout(() => read(false), READ_DEBOUNCE_MS);
    };
    // The first read on the next frame, not after the typing debounce: the
    // body is usually there already (a reopened tab, a toggled rail).
    const raf = requestAnimationFrame(() => read(true));
    const off = subscribeBody(schedule);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
      off();
    };
  }, [enabled, subscribeBody, bodyRef, documentId]);

  useEffect(() => {
    if (!enabled || !items) return undefined;
    if (!items.length) {
      setActiveId(null);
      return undefined;
    }
    const query = () => items.map((it) => bodyRef.current?.querySelector(`#${CSS.escape(it.id)}`)).filter(Boolean);
    let headings = query();
    if (!headings.length) return undefined;
    // The scroller is looked up on every pass, not once: OverlayScrollbars
    // wraps the body in its viewport a moment after mount (`defer`), and a
    // scroller captured before that would never report a scroll.
    const pick = () => {
      if (!headings[0]?.isConnected) headings = query();
      if (!headings.length) return;
      const scroller = scrollerOf(bodyRef.current);
      const top = (scroller ? scroller.getBoundingClientRect().top : 0) + ACTIVE_OFFSET_PX;
      let current = headings[0];
      for (const h of headings) {
        if (h.getBoundingClientRect().top <= top) current = h;
        else break;
      }
      // At the very bottom the last section is the one being read, even if
      // its heading never reached the top band.
      if (scroller && scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 2) {
        current = headings[headings.length - 1];
      }
      setActiveId(current.id);
    };
    // Any scroll of the element that holds this document (captured at the
    // document, so it does not matter which element that is), one pass per frame.
    let frame = 0;
    const onScroll = (e) => {
      const target = e.target;
      if (frame || !bodyRef.current || !(target === document || target?.contains?.(bodyRef.current))) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        pick();
      });
    };
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    window.addEventListener('resize', onScroll);
    pick();
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('resize', onScroll);
    };
  }, [enabled, items, bodyRef, readTick]);

  return { items, activeId };
}
